import React, { useEffect, useState } from "react";
import { debounce, uniqBy } from "lodash";
import { useNavigate } from "react-router-dom";

import Button from "components/common/Button";
import Dropdown from "components/common/Dropdown";
import { useAppDispatch, useAppSelector } from "hooks/storeHooks";
import { AccountPermissions } from "types/permissions";
import { vendorActions } from "redux/Ldb/actions";
import VendorAvatar from "components/common/VendorAvatar";
import Loader from "components/common/Loader";
import CustomTable from "components/common/CustomTable";
import SearchTextField from "components/common/InputField/SearchTextField";
import DeleteSupplierModal from "components/views/Synth2.0/Supplier/DeleteSupplierModal";
import PageTitleSection from "components/common/PageTitleSection";
import Paper from "components/common/Paper";
import TableBodyItem from "components/common/TableBodyItem";

export default function Suppliers() {
    const [ldbVendorList, setLdbVendorList] = useState<{ [key: string]: any }[]>([]);
    const [page, setPage] = useState(1);
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [search, setSearch] = useState("");
    const [confirmDeleteModal, setConfirmDeleteModal] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [deleteSupplier, setDeleteSupplier] = useState<{ id: string; name: string } | null>(null);

    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { fetchingLdbVendor, fetchLdbVendorSuccess } = useAppSelector((state) => state?.vendor);

    useEffect(() => {
        if (Boolean(fetchLdbVendorSuccess)) {
            setLdbVendorList((prev) =>
                page > 1
                    ? uniqBy([...prev, ...fetchLdbVendorSuccess], "_id")
                    : [...fetchLdbVendorSuccess]
            );
        }
    }, [fetchLdbVendorSuccess, dispatch]);

    useEffect(() => {
        dispatch(vendorActions?.fetchVendors(debouncedSearch, "", 10, page));
    }, [dispatch, debouncedSearch, page]);

    useEffect(() => {
        if (page || debouncedSearch) {
            setPage(1);
        }
    }, [page, debouncedSearch]);

    const checkRisk = (num) => {
        if (num >= 0 && num <= 4) {
            return {
                value: "Low",
                img: "https://storage.googleapis.com/heysynth_files/synth_webapp/Frame_2615019_1_oyxqr8.svg",
            };
        } else if (num >= 5 && num <= 7) {
            return {
                value: "Medium",
                img: "https://storage.googleapis.com/heysynth_files/synth_webapp/Frame_2615019_2_yz9dkz.svg",
            };
        }
        if (num >= 8 && num <= 10) {
            return {
                value: "High",
                img: "https://storage.googleapis.com/heysynth_files/synth_webapp/Frame_2615019_3_rj8hoz.svg",
            };
        }
    };

    const handleDebouncedSearch = debounce((e) => {
        setDebouncedSearch(e.target?.value);
    }, 1000);

    const tableHeader = [
        { title: "Supplier", widthClass: "w-[17%]" },
        { title: "Contact", widthClass: "w-[12%]" },
        { title: "Phone number", widthClass: "w-[9.8%]" },
        { title: "Source", widthClass: "w-[15%]" },
        { title: "", widthClass: "w-[5.1%]" },
    ];

    const tableBody = ldbVendorList?.map((vendor, idx) => [
        {
            content: (
                <TableBodyItem>
                    {vendor?.vendorData?.profile?.businessName ? (
                        <div className="flex items-center space-x-3">
                            <VendorAvatar
                                imageSrc={vendor?.vendorData?.profile?.logo}
                                containerClassname="!rounded-none"
                                size={32}
                                name={vendor?.vendorData?.profile?.businessName}
                            />

                            <span className="break-all">
                                {vendor?.vendorData?.profile?.businessName}
                            </span>
                        </div>
                    ) : (
                        "N/A"
                    )}
                </TableBodyItem>
            ),
            cellClickAction: () =>
                navigate(`/dashboard/suppliers/${vendor?.vendorId}`, {
                    state: { internalNavigation: true },
                }),
        },
        {
            content: (
                <TableBodyItem>
                    {vendor?.source === "online" ? (
                        vendor?.vendorData?.profile?.website || "N/A"
                    ) : vendor?.vendorData?.firstName || vendor?.vendorData?.lastName ? (
                        <div className="flex items-center space-x-2">
                            <VendorAvatar
                                imageSrc={vendor?.vendorData?.profile?.avatar}
                                size={24}
                                name={vendor?.vendorData?.firstName || vendor?.vendorData?.lastName}
                            />
                            <span>
                                {vendor?.vendorData?.firstName ?? ""}{" "}
                                {vendor?.vendorData?.lastName ?? ""}
                            </span>
                        </div>
                    ) : (
                        "N/A"
                    )}
                </TableBodyItem>
            ),
        },
        {
            content: (
                <TableBodyItem>
                    <span className="">{`${vendor?.mobileExtension ?? ""}${
                        vendor?.mobile ?? ""
                    }`}</span>
                </TableBodyItem>
            ),
        },
        {
            content: (
                <TableBodyItem>
                    {vendor?.source?.toLowerCase() === "online" ? (
                        <div className="flex items-center space-x-2">
                            <i className="ri-earth-line text-[#142837]"></i>
                            <p className="text-sm font-rocGroteskMedium text-[#142837]">Online</p>
                        </div>
                    ) : (
                        <div className="flex items-center space-x-2">
                            <i className="ri-store-2-line text-[#142837]"></i>
                            <p className="text-sm font-rocGroteskMedium text-[#142837]">Offline</p>
                        </div>
                    )}
                </TableBodyItem>
            ),
        },

        {
            content: (
                <TableBodyItem onClick={(e) => e.stopPropagation()}>
                    <Dropdown
                        dropdown={<i className="ri-more-2-fill text-lg text-slate-500"></i>}
                        dropdownClassName={"!w-full !mb-10 !-mt-7"}
                        dropdownContainerClasses={`shadow-cardShadow-2 border-0 rounded !max-h-fit left-[-100px] `}
                        dropdownItemsClasses={``}
                        name={"faq-dropdown"}
                        dropdownOptions={[
                            {
                                label: "Edit Details",
                                value: "Edit details",
                                action: () => {
                                    navigate(
                                        `/dashboard/suppliers/edit-supplier/${vendor?.vendorId}`,
                                        { state: { internalNavigation: true } }
                                    );
                                },
                            },
                            {
                                label: "View Details",
                                value: "view details",
                                action: () => {
                                    navigate(`/dashboard/suppliers/${vendor?.vendorId}`, {
                                        state: { internalNavigation: true },
                                    });
                                },
                            },
                            {
                                label: "Delete supplier",
                                value: "Delete supplier",
                                action: () => {
                                    setConfirmDeleteModal(true);
                                    setShowModal(true);
                                    setDeleteSupplier({
                                        id: vendor?.vendorId,
                                        name: vendor?.vendorData?.profile?.businessName,
                                    });
                                },
                            },
                        ]}
                    />
                </TableBodyItem>
            ),
        },
    ]);

    const renderEmptyTable = () => {
        return ldbVendorList?.length < 1 && debouncedSearch ? (
            <div className="flex items-center justify-center flex-col mt-20">
                <p className="text-gm-40 text-sm font-rocGroteskMedium max-w-[330px] text-center mb-6">
                    No data matches your search query
                </p>
            </div>
        ) : (
            <div className="flex items-center justify-center flex-col mt-20">
                <i className="ri-user-line text-gm-40 text-xl font-rocGroteskMedium mb-6"></i>
                <p className="text-gm-40 text-sm font-rocGroteskMedium max-w-[330px] text-center mb-6">
                    It appears you haven’t onboarded any suppliers yet. Begin by adding suppliers to
                    populate this section and streamline your process.
                </p>
                <Button
                    btnText={"Add Supplier"}
                    btnPermission={[
                        AccountPermissions.all_access,
                        AccountPermissions.create_suppliers,
                    ]}
                    type={"button"}
                    btnClassname="!py-2 !px-4 !w-fit !whitespace-nowrap max-sm:w-full"
                    onClick={() =>
                        navigate("/dashboard/suppliers/create-supplier", {
                            state: { internalNavigation: true },
                        })
                    }
                />
            </div>
        );
    };

    return (
        <Paper>
            <PageTitleSection
                leftComp={<h3 className="text-xl font-rocGroteskMedium">Supplier</h3>}
            />

            <div className="w-full flex items-center gap-3 mt-[24px]">
                <SearchTextField
                    name={"search"}
                    type={"text"}
                    value={search}
                    inputPlaceholder={"Search supplier"}
                    containerClassname={""}
                    onChange={(e) => setSearch(e.target?.value)}
                    onInput={handleDebouncedSearch}
                />
                <Button
                    btnText={"Add Supplier"}
                    btnPermission={[
                        AccountPermissions.all_access,
                        AccountPermissions.create_suppliers,
                    ]}
                    type={"button"}
                    btnTextClassName="block"
                    btnClassname="!py-2 !px-4 !w-fit !whitespace-nowrap max-sm:w-full"
                    onClick={() =>
                        navigate("/dashboard/suppliers/create-supplier", {
                            state: { internalNavigation: true },
                        })
                    }
                />
            </div>

            <div className="mt-[16px]">
                <div className="hidden lg:block">
                    <CustomTable
                        tableBody={tableBody}
                        tableHeader={tableHeader}
                        isScrollable={false}
                        isCellBordered
                        isCellSelectable={false}
                        isAllSelectable={false}
                        headerContainerClass="!bg-slate-100  !border-slate-200 !rounded-[8px] !border"
                        headerItemClass="font-rocGroteskMedium "
                        tableClass="mb-12 !border-slate-200"
                        isLoading={fetchingLdbVendor && !debouncedSearch}
                        renderEmptyTable={() => renderEmptyTable()}
                    />
                </div>
                <div className="lg:hidden space-y-2.5">
                    {ldbVendorList?.map((vendor, idx) => {
                        return (
                            <div
                                className="border border-slate-200 rounded px-4 py-3.5 font-rocGroteskMedium flex gap-2 justify-between items-center"
                                key={idx}
                            >
                                <div className="w-full">
                                    <div className="flex gap-2">
                                        <div
                                            className={`text-gm-50 text-sm border-r border-slate-200 pr-4`}
                                        >
                                            <p>{vendor?.vendorData?.profile?.businessName}</p>
                                        </div>

                                        <div className={`text-gm-50 text-sm`}>
                                            <div className="flex items-center space-x-2">
                                                {(vendor?.vendorData?.firstName ||
                                                    vendor?.vendorData?.secondName) && (
                                                    <VendorAvatar
                                                        imageSrc={
                                                            vendor?.vendorData?.profile?.avatar
                                                        }
                                                        size={24}
                                                        name={vendor?.vendorData?.firstName}
                                                    />
                                                )}
                                                <span>
                                                    {vendor?.vendorData?.firstName ?? ""}{" "}
                                                    {vendor?.vendorData?.lastName ?? ""}
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex gap-2 mt-3">
                                        <div
                                            className={`text-[#64748B] text-xs border-r border-slate-200 pr-4`}
                                        >
                                            <p className="">{vendor?.vendorData?.email}</p>
                                        </div>
                                        <div className="text-[#64748B] text-xs">
                                            <p>
                                                {" "}
                                                {`${vendor?.mobileExtension ?? ""}${
                                                    vendor?.mobile ?? ""
                                                }`}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                                <div className={`font-rocGroteskMedium border-slate-100 text-sm  `}>
                                    {vendor?.vendorData?.riskDetails?.riskScore && (
                                        <img
                                            src={
                                                checkRisk(
                                                    vendor?.vendorData?.riskDetails?.riskScore
                                                )?.img
                                            }
                                            className="h-[18px] w-[24px]"
                                            alt=""
                                        />
                                    )}
                                </div>

                                <div
                                    className={`  text-sm text-slate-800 `}
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <Dropdown
                                        dropdown={
                                            <i className="ri-more-2-fill text-lg text-slate-500"></i>
                                        }
                                        dropdownClassName={"!w-full !mb-10 !-mt-7"}
                                        dropdownContainerClasses={`shadow-cardShadow-2 border-0 rounded !max-h-fit left-[-100px] `}
                                        dropdownItemsClasses={``}
                                        name={"faq-dropdown"}
                                        dropdownOptions={[
                                            {
                                                label: "Edit Details",
                                                value: "Edit details",
                                                action: () => {
                                                    navigate(
                                                        `/dashboard/suppliers/edit-supplier/${vendor?.vendorId}`,
                                                        {
                                                            state: {
                                                                internalNavigation: true,
                                                            },
                                                        }
                                                    );
                                                },
                                            },
                                            {
                                                label: "View Details",
                                                value: "view details",
                                                action: () => {
                                                    navigate(
                                                        `/dashboard/suppliers/${vendor?.vendorId}`,
                                                        {
                                                            state: {
                                                                internalNavigation: true,
                                                            },
                                                        }
                                                    );
                                                },
                                            },
                                            {
                                                label: "Delete supplier",
                                                value: "Delete supplier",
                                                action: () => {
                                                    setConfirmDeleteModal(true);
                                                    setShowModal(true);
                                                    setDeleteSupplier({
                                                        id: vendor?.vendorId,
                                                        name: vendor?.vendorData?.profile
                                                            ?.businessName,
                                                    });
                                                },
                                            },
                                        ]}
                                    />
                                </div>
                            </div>
                        );
                    })}
                    {fetchingLdbVendor && !debouncedSearch && (
                        <div className="flex justify-center my-4">
                            <Loader size={4} />
                        </div>
                    )}
                    {renderEmptyTable()}
                </div>

                {showModal && (
                    <DeleteSupplierModal
                        setConfirmDeleteModal={setConfirmDeleteModal}
                        confirmDeleteModal={confirmDeleteModal}
                        deleteSupplier={deleteSupplier}
                    />
                )}
            </div>
        </Paper>
    );
}
